import { Link, useParams, useSearchParams } from 'react-router-dom';

import { HTTP_STATUS } from '@/shared/constants/app/http.constant.ts';

import { usePumsemDiff } from '../api/use-pumsem.query';
import { PumsemBody } from '../components/PumsemBody';
import { unplacedNotes } from '../lib/pumsemBody';
import { EDITION_PARAM, pumsemItemPath } from '../lib/pumsemNavigation';
import { PUMSEM_ERROR, codeOf, statusOf } from '../lib/pumsemError';
import { PumsemMessage } from './PumsemMessage';

/**
 * 직전 판 대비 비교.
 *
 * **임의 두 판을 고르게 하지 않는다.** 법제처 신구조문대비표도 임의 시점이 아니라
 * 공포(개정 이벤트) 단위로만 제공한다. 판이 늘수록 조합이 폭발하고, 실무 질문
 * ("이번에 뭐 바뀌었나")은 직전 판 대비로 거의 전부 덮인다.
 *
 * 왼쪽(이전 판)은 개정 표시를 끈다 — 변경 강조는 "지금 판이 무엇을 바꿨나"를
 * 말하는 것이라 양쪽에 다 칠하면 어느 쪽이 새것인지 알 수 없다.
 */
export function PumsemDiffView() {
  const { division, code } = useParams();
  const [params] = useSearchParams();
  const edition = params.get(EDITION_PARAM);

  const { data: diff, isPending, isError, error } = usePumsemDiff(
    division,
    code,
    edition,
  );

  if (isError) {
    if (
      statusOf(error) === HTTP_STATUS.NOT_FOUND &&
      codeOf(error) === PUMSEM_ERROR.NO_PUBLISHED_EDITION
    ) {
      return (
        <PumsemMessage
          title="아직 공개된 개정판이 없습니다."
          body="비교하려면 개정판이 둘 이상 공개돼 있어야 합니다."
        />
      );
    }
    return statusOf(error) === HTTP_STATUS.NOT_FOUND ? (
      <PumsemMessage
        title="비교할 직전 개정판이 없습니다."
        body="이 판이 첫 판이거나, 직전 판에 이 항목이 없습니다."
      />
    ) : (
      <PumsemMessage
        title="비교를 불러오지 못했습니다."
        body="잠시 후 다시 시도해 주세요."
      />
    );
  }

  if (isPending || !diff) {
    return (
      <div className="space-y-3 p-8" aria-hidden>
        <div className="h-5 w-1/2 rounded bg-neutral-100 dark:bg-white/8" />
        <div className="h-64 w-full rounded-xl bg-neutral-100 dark:bg-white/8" />
      </div>
    );
  }

  return (
    <div className="min-w-0 p-5 sm:p-8">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-[24px] leading-tight font-black tracking-tight text-neutral-900 dark:text-neutral-50">
          <span className="tabular-nums">{diff.code}</span>
          <span className="px-2 text-neutral-300">·</span>
          {diff.title}
        </h1>
        <Link
          to={pumsemItemPath(diff.division.slug, diff.code, edition)}
          className="ml-auto rounded-lg border border-neutral-200 px-2.5 py-1.5 text-[13.5px] text-neutral-700 transition-colors hover:bg-neutral-50 dark:border-white/12 dark:text-neutral-200 dark:hover:bg-white/5"
        >
          단일 보기로
        </Link>
      </div>

      <p className="mt-2.5 flex flex-wrap items-center gap-2 text-[13.5px] text-neutral-500">
        <span className="rounded-md bg-neutral-100 px-2 py-1 font-bold text-neutral-600 dark:bg-white/8 dark:text-neutral-300">
          직전 판 대비
        </span>
        <span>
          {diff.before.edition.label} → {diff.after.edition.label}
        </span>
      </p>

      {diff.changeSummary && (
        <p className="mt-4 rounded-xl border border-warning/40 bg-warning/10 px-4 py-3 text-[14.5px] leading-6 text-amber-900 dark:text-amber-200">
          {diff.changeSummary}
        </p>
      )}

      <div className="mt-2 grid grid-cols-1 gap-5 xl:grid-cols-2">
        <section>
          <h2 className="mt-5 text-[14px] font-bold text-neutral-500">
            {diff.before.edition.label}
          </h2>
          {diff.before.body.length === 0 ? (
            <p className="mt-4 rounded-xl border border-dashed border-neutral-200 px-4 py-8 text-center text-[14px] text-neutral-400 dark:border-white/12">
              직전 판에 없던 항목입니다.
            </p>
          ) : (
            <PumsemBody body={diff.before.body} hideChanges />
          )}
          <NoteList notes={unplacedNotes(diff.before.notes, diff.before.body)} />
        </section>

        <section>
          <h2 className="mt-5 text-[14px] font-bold text-brand-700 dark:text-brand-300">
            {diff.after.edition.label}
          </h2>
          <PumsemBody body={diff.after.body} />
          <NoteList notes={unplacedNotes(diff.after.notes, diff.after.body)} />
        </section>
      </div>
    </div>
  );
}

/** 본문에 자리를 잡은 [주]는 `PumsemBody` 가 그렸다. 남은 것만 여기 둔다. */
function NoteList({ notes }: { notes: string[] }) {
  if (notes.length === 0) return null;
  return (
    <ul className="space-y-1.5 rounded-xl border border-neutral-200 px-4 py-3 text-[14px] leading-6 text-neutral-700 dark:border-white/12 dark:text-neutral-300">
      {notes.map((note) => (
        <li key={note}>{note}</li>
      ))}
    </ul>
  );
}
